import React, { useEffect, useMemo, useState } from 'react';
import { FAQ_AUDIENCE_GENERAL, FAQ_AUDIENCE_OPTIONS, faqMatchesAudience, getFaqAudienceLabel } from '../constants/helpFaqAudiences';
import './FaqByRoleSection.css';

const FaqByRoleSection = ({
  faqs = [],
  loading = false,
  initialAudience = 'admin',
  searchTerm = '',
  onAudienceChange,
}) => {
  const [activeAudience, setActiveAudience] = useState(initialAudience);

  useEffect(() => {
    setActiveAudience(initialAudience);
  }, [initialAudience]);

  const activeMeta = useMemo(
    () => FAQ_AUDIENCE_OPTIONS.find((item) => item.slug === activeAudience) || FAQ_AUDIENCE_OPTIONS[0],
    [activeAudience]
  );

  const filteredFaqs = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const list = faqs.filter((faq) => faqMatchesAudience(faq, activeAudience));
    if (!term) return list;
    return list.filter((faq) => {
      const haystack = [faq.question, faq.answer].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(term);
    });
  }, [faqs, activeAudience, searchTerm]);

  const introHint =
    activeAudience === FAQ_AUDIENCE_GENERAL
      ? `${filteredFaqs.length} general question${filteredFaqs.length === 1 ? '' : 's'}`
      : `${filteredFaqs.length} question${filteredFaqs.length === 1 ? '' : 's'} · includes general FAQs`;

  const handleAudienceChange = (slug) => {
    setActiveAudience(slug);
    onAudienceChange?.(slug);
  };

  return (
    <div className="faq-by-role-section">
      <div className="faq-by-role-intro card border-0 shadow-sm">
        <h2 className="h4 fw-bold mb-2">{activeMeta.label}</h2>
        <p className="text-muted mb-2">{activeMeta.subtitle}</p>
        <p className="small text-muted mb-0">{introHint}</p>
      </div>

      <div className="row g-4">
        <div className="col-lg-3">
          <div className="card faq-by-role-sidebar border-0 shadow-sm sticky-lg-top">
            <div className="card-header bg-white fw-bold">Select user type</div>
            <div className="list-group list-group-flush">
              {FAQ_AUDIENCE_OPTIONS.map((role) => (
                <button
                  key={role.slug}
                  type="button"
                  className={`list-group-item list-group-item-action faq-by-role-btn ${
                    activeAudience === role.slug ? 'active' : ''
                  }`}
                  onClick={() => handleAudienceChange(role.slug)}
                >
                  <span className="faq-by-role-icon" aria-hidden="true">
                    {role.icon}
                  </span>
                  <span>
                    <span className="d-block fw-semibold">{role.label}</span>
                    <small className="text-muted">{role.subtitle}</small>
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="col-lg-9">
          <div className="card help-faq-card border-0 shadow-sm">
            <div className="card-body">
              {loading ? (
                <div className="help-skeleton" />
              ) : filteredFaqs.length === 0 ? (
                <div className="help-empty">No FAQs for {getFaqAudienceLabel(activeAudience)} yet.</div>
              ) : (
                <div className="accordion" id="faqByRoleAccordion">
                  {filteredFaqs.map((faq) => (
                    <div className="accordion-item border-0 mb-2" key={faq.id}>
                      <h2 className="accordion-header">
                        <button
                          className="accordion-button collapsed rounded"
                          type="button"
                          data-bs-toggle="collapse"
                          data-bs-target={`#faq-role-${faq.id}`}
                          aria-expanded="false"
                          aria-controls={`faq-role-${faq.id}`}
                        >
                          {faq.question}
                        </button>
                      </h2>
                      <div
                        id={`faq-role-${faq.id}`}
                        className="accordion-collapse collapse"
                        data-bs-parent="#faqByRoleAccordion"
                      >
                        <div
                          className="accordion-body text-muted help-article-content"
                          dangerouslySetInnerHTML={{ __html: faq.answer }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FaqByRoleSection;
